import { bot, lobbies } from '../../api/endpoints';
import type { BotDifficultyKey, BotSeat, MutationResponse } from '../../api/types';

/**
 * Everything the page can ask the bot to do to a live lobby, as one union so
 * a single mutation, one toast site and one test cover all of them.
 */
export type LobbyOperation =
  | { kind: 'flip' }
  | { kind: 'shuffle' }
  | { kind: 'kick'; steamId: string; name?: string }
  | { kind: 'unseat'; steamId: string; name?: string }
  | { kind: 'seat_bot'; seat: BotSeat; slot?: number }
  | { kind: 'bot_slot'; team: 'radiant' | 'dire'; slot: number; difficulty: BotDifficultyKey }
  | { kind: 'fill_bots'; difficulty: BotDifficultyKey };

export type LobbyOperationApi = {
  flipTeams: (botId: string) => Promise<MutationResponse>;
  shuffleTeams: (botId: string) => Promise<MutationResponse>;
  kick: (botId: string, payload: { steam_id: string; from_team_only?: boolean }) => Promise<MutationResponse>;
  seatBot: (botId: string, payload: { seat: BotSeat; slot?: number }) => Promise<MutationResponse>;
  setBotSlot: (
    botId: string,
    payload: { team: 'radiant' | 'dire'; slot: number; difficulty?: BotDifficultyKey },
  ) => Promise<MutationResponse>;
  fillBots: (payload: { bot_id: string; bot_difficulty: FillDifficulty }) => Promise<MutationResponse>;
};

type FillDifficulty = 'PASSIVE' | 'EASY' | 'MEDIUM' | 'HARD' | 'UNFAIR' | 'NORMAL';

const defaultApi: LobbyOperationApi = {
  flipTeams: bot.flipTeams,
  shuffleTeams: bot.shuffleTeams,
  kick: bot.kick,
  seatBot: bot.seatBot,
  setBotSlot: bot.setBotSlot,
  fillBots: lobbies.fillBots,
};

/** fill-bots predates the other endpoints and spells its difficulty in capitals. */
export function fillDifficulty(difficulty: BotDifficultyKey): FillDifficulty {
  return difficulty.toUpperCase() as FillDifficulty;
}

export function runLobbyOperation(
  botId: string,
  op: LobbyOperation,
  api: LobbyOperationApi = defaultApi,
): Promise<MutationResponse> {
  switch (op.kind) {
    case 'flip':
      return api.flipTeams(botId);
    case 'shuffle':
      return api.shuffleTeams(botId);
    case 'kick':
      return api.kick(botId, { steam_id: op.steamId });
    case 'unseat':
      return api.kick(botId, { steam_id: op.steamId, from_team_only: true });
    case 'seat_bot':
      return api.seatBot(botId, op.slot === undefined ? { seat: op.seat } : { seat: op.seat, slot: op.slot });
    case 'bot_slot':
      return api.setBotSlot(botId, { team: op.team, slot: op.slot, difficulty: op.difficulty });
    case 'fill_bots':
      return api.fillBots({ bot_id: botId, bot_difficulty: fillDifficulty(op.difficulty) });
  }
}

export const BOT_SEAT_LABELS: Record<BotSeat, string> = {
  spectator: 'Spectator',
  player_pool: 'Unassigned',
  broadcaster: 'Broadcaster',
  coach_radiant: 'Radiant coach',
  coach_dire: 'Dire coach',
  radiant: 'Radiant slot',
  dire: 'Dire slot',
};

export const BOT_SEATS: readonly BotSeat[] = [
  'spectator',
  'player_pool',
  'broadcaster',
  'coach_radiant',
  'coach_dire',
  'radiant',
  'dire',
];

const SIDE_LABELS = { radiant: 'Radiant', dire: 'Dire' } as const;

/** Toast title once the API accepted the operation. */
export function describeLobbyOperation(op: LobbyOperation): string {
  switch (op.kind) {
    case 'flip':
      return 'Sides swapped';
    case 'shuffle':
      return 'Teams shuffled';
    case 'kick':
      return `Kicked ${op.name || op.steamId}`;
    case 'unseat':
      return `${op.name || op.steamId} moved to unassigned`;
    case 'seat_bot': {
      const label = BOT_SEAT_LABELS[op.seat];
      return op.slot !== undefined && (op.seat === 'radiant' || op.seat === 'dire')
        ? `Bot moved to ${label} ${op.slot}`
        : `Bot moved to ${label}`;
    }
    case 'bot_slot':
      return `${op.difficulty} AI bot added to ${SIDE_LABELS[op.team]} slot ${op.slot}`;
    case 'fill_bots':
      return `Filling empty slots with ${op.difficulty} AI bots`;
  }
}
